import React, { useRef, useState } from 'react'
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import IosShare from '@mui/icons-material/IosShare'
import { isAxiosError } from 'axios'
import { enqueueSnackbar } from 'notistack'
import { axiosClient } from 'core/api/axiosClient'
import { useIsMobile } from 'hooks/screen/useIsMobile'
import { TOUCH_MIN } from 'core/theme.tokens'
import { downloadTranscript, formatExportStamp, shareableMessages } from './assistantExport'

const MAX_SHARE_MINUTES = 30 * 24 * 60
const MIN_SHARE_MINUTES = 15

const PRESETS: Array<{ id: string; label: string; minutes: number | null }> = [
  { id: '60', label: '1 hour', minutes: 60 },
  { id: '1440', label: '24 hours', minutes: 24 * 60 },
  { id: '10080', label: '7 days', minutes: 7 * 24 * 60 },
  { id: '43200', label: '30 days', minutes: 30 * 24 * 60 },
  { id: 'custom', label: 'Custom', minutes: null },
]

type ShareResult = {
  url: string
  expiresAt: number
  passcode: string
}

function minutesFromChoice(choice: string | null, customLocal: string, now = Date.now()): number | null {
  if (!choice) return null
  if (choice !== 'custom') {
    const minutes = Number(choice)
    return Number.isInteger(minutes) ? minutes : null
  }
  if (!customLocal) return null
  const at = new Date(customLocal).getTime()
  if (!Number.isFinite(at)) return null
  const minutes = Math.round((at - now) / 60_000)
  if (minutes < MIN_SHARE_MINUTES || minutes > MAX_SHARE_MINUTES) return null
  return minutes
}

/** The shared client returns the JSON body, while its types still say AxiosResponse. */
function shareCreated(value: unknown): { path: string; expiresAt: number } | null {
  const record =
    value &&
    typeof value === 'object' &&
    'data' in value &&
    value.data &&
    typeof value.data === 'object'
      ? value.data
      : value
  if (!record || typeof record !== 'object') return null
  const path = 'path' in record ? record.path : undefined
  const expiresAt = 'expiresAt' in record ? record.expiresAt : undefined
  if (typeof path !== 'string' || !path.startsWith('/') || typeof expiresAt !== 'number') return null
  return { path, expiresAt }
}

function errorText(err: unknown, fallback: string): string {
  if (isAxiosError(err)) {
    const message = err.response?.data?.error
    if (typeof message === 'string' && message.trim()) return message
  }
  if (err instanceof Error && err.message.trim()) return err.message
  return fallback
}

type AssistantShareButtonProps = {
  title: string
  messages: Array<{ role: string; content: string }>
}

export const AssistantShareButton: React.FC<AssistantShareButtonProps> = ({ title, messages }) => {
  const isMobile = useIsMobile()
  const transcript = shareableMessages(messages)
  const canShare = transcript.length > 0
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [choice, setChoice] = useState<string | null>(null)
  const [customLocal, setCustomLocal] = useState('')
  const [passcode, setPasscode] = useState('')
  const [noSensitiveData, setNoSensitiveData] = useState(false)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<ShareResult | null>(null)
  const openLinkAfterMenu = useRef(false)

  const minutes = minutesFromChoice(choice, customLocal)
  const customInvalid = choice === 'custom' && customLocal !== '' && minutes == null
  const passcodeTrimmed = passcode.trim()
  const passcodeInvalid =
    passcodeTrimmed.length > 0 && (passcodeTrimmed.length < 4 || passcodeTrimmed.length > 64)
  const publicLink = noSensitiveData && passcodeTrimmed.length === 0
  const canCreateLink = minutes != null && !passcodeInvalid && (passcodeTrimmed.length >= 4 || publicLink)

  const closeDialog = () => {
    if (busy) return
    setDialogOpen(false)
    setChoice(null)
    setCustomLocal('')
    setPasscode('')
    setNoSensitiveData(false)
    setResult(null)
  }

  const download = async (format: 'pdf' | 'md' | 'txt') => {
    setAnchor(null)
    try {
      await downloadTranscript(format, title, transcript)
    } catch {
      enqueueSnackbar('Could not download this chat', { variant: 'error' })
    }
  }

  const createLink = async () => {
    if (!canCreateLink || minutes == null) return
    setBusy(true)
    try {
      const created = shareCreated(
        await axiosClient.post('/portal/admin/assistant/shares', {
          title,
          messages: transcript,
          expiresInMinutes: minutes,
          ...(passcodeTrimmed ? { passcode: passcodeTrimmed } : { confirmNoSensitiveData: true }),
        }),
      )
      if (!created) {
        enqueueSnackbar('Could not create the link', { variant: 'error' })
        return
      }
      setResult({
        url: `${window.location.origin}${created.path}`,
        expiresAt: created.expiresAt,
        passcode: passcodeTrimmed,
      })
    } catch (err) {
      enqueueSnackbar(errorText(err, 'Could not create the link'), { variant: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const copy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      enqueueSnackbar(`${label} copied`, { variant: 'success' })
    } catch {
      enqueueSnackbar(`Could not copy the ${label.toLowerCase()}`, { variant: 'error' })
    }
  }

  return (
    <>
      <Tooltip title={canShare ? 'Share' : 'Start a chat before sharing'}>
        <span data-assistant-demo='share'>
          <IconButton
            size='small'
            aria-label='Share chat'
            disabled={!canShare}
            onClick={event => setAnchor(event.currentTarget)}
            sx={{ width: isMobile ? 40 : TOUCH_MIN, height: isMobile ? 40 : TOUCH_MIN }}
          >
            <IosShare fontSize={isMobile ? 'small' : 'medium'} />
          </IconButton>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        TransitionProps={{
          onExited: () => {
            if (!openLinkAfterMenu.current) return
            openLinkAfterMenu.current = false
            setResult(null)
            setChoice(null)
            setPasscode('')
            setNoSensitiveData(false)
            setDialogOpen(true)
          },
        }}
      >
        <MenuItem onClick={() => void download('pdf')}>Download PDF</MenuItem>
        <MenuItem onClick={() => void download('md')}>Download Markdown</MenuItem>
        <MenuItem onClick={() => void download('txt')}>Download TXT</MenuItem>
        <MenuItem
          onClick={() => {
            openLinkAfterMenu.current = true
            setAnchor(null)
          }}
        >
          Create link
        </MenuItem>
      </Menu>
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth='xs' fullScreen={isMobile}>
        <DialogTitle>{result ? 'Link ready' : 'Create a link'}</DialogTitle>
        <DialogContent>
          {result ? (
            <Stack spacing={1.5} sx={{ pt: 0.5 }}>
              <Alert severity={result.passcode ? 'info' : 'warning'}>
                This link is public. Anyone who has it can read this chat until{' '}
                {formatExportStamp(new Date(result.expiresAt))}.
                {result.passcode
                  ? ' The link does not include the passcode — send that separately.'
                  : ' You confirmed this chat has no sensitive information, so it opens with no code.'}
              </Alert>
              <TextField value={result.url} label='Link' fullWidth InputProps={{ readOnly: true }} />
              {result.passcode ? (
                <TextField value={result.passcode} label='Passcode' fullWidth InputProps={{ readOnly: true }} />
              ) : null}
            </Stack>
          ) : (
            <Stack spacing={1.5} sx={{ pt: 0.5 }}>
              <Alert severity='warning'>
                This link will be public. Anyone who has it can read the chat. If it includes customer,
                price, or account details, add a passcode. Leave the passcode blank only if you confirm
                there is no sensitive information.
              </Alert>
              <Typography variant='body2'>How long should this link work?</Typography>
              <RadioGroup value={choice ?? ''} onChange={event => setChoice(event.target.value)}>
                {PRESETS.map(preset => (
                  <FormControlLabel key={preset.id} value={preset.id} control={<Radio />} label={preset.label} />
                ))}
              </RadioGroup>
              {choice === 'custom' ? (
                <TextField
                  type='datetime-local'
                  label='Expiration'
                  value={customLocal}
                  onChange={event => setCustomLocal(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  error={customInvalid}
                  helperText={customInvalid ? 'Choose a time from 15 minutes to 30 days from now.' : ' '}
                  fullWidth
                />
              ) : (
                <Typography variant='caption' color='text.secondary'>
                  Pick a time. The link stops working after that.
                </Typography>
              )}
              <TextField
                label='Passcode'
                value={passcode}
                onChange={event => setPasscode(event.target.value)}
                error={passcodeInvalid}
                helperText={
                  passcodeInvalid
                    ? 'Use 4–64 characters.'
                    : 'Required unless you confirm there is no sensitive information.'
                }
                fullWidth
                autoComplete='off'
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={noSensitiveData}
                    onChange={event => setNoSensitiveData(event.target.checked)}
                  />
                }
                label='This chat has no sensitive information. Create a public link with no passcode.'
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {result ? (
            <>
              <Button onClick={closeDialog}>Done</Button>
              {result.passcode ? (
                <Button onClick={() => void copy(result.passcode, 'Passcode')}>Copy passcode</Button>
              ) : null}
              <Button variant='contained' onClick={() => void copy(result.url, 'Link')}>
                Copy link
              </Button>
            </>
          ) : (
            <>
              <Button onClick={closeDialog} disabled={busy}>
                Cancel
              </Button>
              <Button
                variant='contained'
                disabled={!canCreateLink || busy}
                onClick={() => void createLink()}
              >
                {busy ? 'Creating…' : 'Create link'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}
